import { useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ArrowLeft, MoreHorizontal, Plus } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useAdminAnnouncements,
  useDeleteAnnouncement,
  type AnnouncementAdminResponse,
} from "@/hooks/useAdminAnnouncements";
import {
  AnnouncementFormDrawer,
  type Mode,
} from "@/components/admin/AnnouncementFormDrawer";

export const Route = createFileRoute("/admin/announcements")({
  component: AdminAnnouncementsPage,
});

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function emailStatusCell(a: AnnouncementAdminResponse): string {
  if (a.email_sent_at) {
    return new Date(a.email_sent_at).toLocaleString();
  }
  if (a.email_requested) return "scheduled";
  return "—";
}

export function AdminAnnouncementsPage() {
  const router = useRouter();
  const { data: list, isLoading, error } = useAdminAnnouncements();
  const del = useDeleteAnnouncement();

  // Drawer mode: null = closed; otherwise "new" or edit form payload.
  const [mode, setMode] = useState<Mode | null>(null);

  function openNew() {
    setMode("new");
  }

  function openEdit(a: AnnouncementAdminResponse) {
    setMode({ kind: "edit", announcement: a });
  }

  function handleDelete(a: AnnouncementAdminResponse) {
    if (!window.confirm(`Delete announcement "${a.title}"?`)) return;
    del.mutate(a.id, {
      onSuccess: () => toast.success("Announcement deleted."),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Delete failed"),
    });
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => router.history.back()}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-2xl font-bold">Announcements</h1>
        <div className="ml-auto">
          <Button size="sm" onClick={openNew}>
            <Plus className="size-4 mr-2" />
            New announcement
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">
            {error instanceof Error
              ? error.message
              : "Failed to load announcements"}
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <p className="text-muted-foreground">Loading announcements...</p>
      )}

      {list && (
        <Card>
          <CardHeader>
            <CardTitle>Announcements ({list.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Title</th>
                    <th className="pb-2 font-medium">Target</th>
                    <th className="pb-2 font-medium">Created</th>
                    <th className="pb-2 font-medium">Reach</th>
                    <th className="pb-2 font-medium">Email</th>
                    <th className="pb-2 font-medium w-10" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((a) => (
                    <tr
                      key={a.id}
                      className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => openEdit(a)}
                    >
                      <td className="py-2 font-medium">{a.title}</td>
                      <td className="py-2">
                        {a.all_users ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            all users
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {a.target_tags.map((t) => (
                              <span
                                key={t}
                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {relativeTime(a.created_at)}
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {a.ack_count}/{a.target_user_count}
                      </td>
                      <td className="py-2 text-muted-foreground text-xs">
                        {emailStatusCell(a)}
                      </td>
                      <td
                        className="py-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RowKebab
                          announcement={a}
                          onEdit={() => openEdit(a)}
                          onDelete={() => handleDelete(a)}
                        />
                      </td>
                    </tr>
                  ))}
                  {list.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No announcements yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {mode !== null && (
        <AnnouncementFormDrawer
          mode={mode}
          open={true}
          onClose={() => setMode(null)}
        />
      )}
    </div>
  );
}

function RowKebab({
  announcement,
  onEdit,
  onDelete,
}: {
  announcement: AnnouncementAdminResponse;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);

  function run(fn: () => void) {
    setOpen(false);
    fn();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`kebab-${announcement.id}`}
          className="rounded p-1 hover:bg-muted transition-colors"
          aria-label="Announcement actions"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={4}
        className="w-32 p-1 text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => run(onEdit)}
          className="w-full text-left rounded-sm px-2 py-1.5 transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          Edit
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={() => run(onDelete)}
          className="w-full text-left rounded-sm px-2 py-1.5 transition-colors text-destructive hover:bg-destructive/10"
        >
          Delete
        </button>
      </PopoverContent>
    </Popover>
  );
}
